import { useContext, useState } from 'react';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { useTranslation } from '../../i18n/useTranslation';
import { AuthContext } from '../../context/AuthContext';
import { useOptionalCapture } from './CaptureContext';

const DISMISS_KEY_PREFIX = 'reefguide:voiceConsentOfferDismissed:';

function readDismissed(userId: string | null): boolean {
  if (!userId) return false;
  try {
    return window.localStorage.getItem(`${DISMISS_KEY_PREFIX}${userId}`) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(userId: string | null): void {
  if (!userId) return;
  try {
    window.localStorage.setItem(`${DISMISS_KEY_PREFIX}${userId}`, '1');
  } catch {
    // storage unavailable: the offer just shows again next time
  }
}

export interface VoiceConsentOfferProps {
  className?: string;
}

/**
 * The proactive voice consent offer (usability review H5): offered on Journal
 * and To review so the consent is not first asked at the tank. Static notice
 * (live=off); gone once consent is given or the offer was dismissed (per user).
 */
export function VoiceConsentOffer({ className }: VoiceConsentOfferProps) {
  const { t } = useTranslation();
  const capture = useOptionalCapture();
  const auth = useContext(AuthContext);
  const userId = auth?.user?.userId ?? null;
  const [dismissed, setDismissed] = useState(() => readDismissed(userId));

  if (!capture?.available || !capture.settings || capture.settings.consent.given || dismissed) return null;

  return (
    <Alert
      tone="info"
      live="off"
      compact
      className={className}
      title={t('voice.consent.offerTitle')}
      action={
        <Button variant="secondary" size="sm" onClick={() => capture.openConsent({ thenRecord: false })}>
          {t('voice.consent.allowOnly')}
        </Button>
      }
      onDismiss={() => {
        writeDismissed(userId);
        setDismissed(true);
      }}
      dismissLabel={String(t('voice.consent.offerDismiss'))}
    >
      {t('voice.consent.offerBody')}
    </Alert>
  );
}
